import React from 'react';
import { P1, Header1 } from 'components/typography';
import { Link } from 'react-scroll';
import ActiveLink from 'components/active-link/active-link';
import LocaleContext from 'lib/locale-context';
import { useRouter } from 'next/router';
import useMediaQuery, { MediaQueryWidth } from 'lib/use-media-query';
import Icon, { IconName } from 'components/icon/icon';
import cx from 'classnames';
import { Lexeme } from '@bumble/localization/components';
import A11yVisuallyHidden from 'components/a11y/a11y-visually-hidden';
import FocusTrap from 'focus-trap-react';
import navigationLexemes from 'components/lexemes/navigation';

type Props = {
    items?: {
        type?: string;
        isHidden?: boolean;
        url?: string;
        title?: string;
    }[];
    onSetActive?: any;
    offset: number;
    isOpened: boolean;
    onChange?: (isOpened: boolean) => void;
};

const PageNavigation: React.FC<Props> = ({ items, offset, onSetActive, isOpened, onChange }) => {
    const router = useRouter();
    const lang = React.useContext(LocaleContext);
    const isBreakpoint = useMediaQuery(MediaQueryWidth['desktop-md']);
    const [showMenu, setMenuVisible] = React.useState(false);
    const [anchorLinks, setAnchorLinks] = React.useState(true);

    const _offset: number = offset * -1 || 0;
    const Style = isBreakpoint ? Header1 : P1;

    const pageNavigationClassnames = cx({
        'page-navigation': true,
        'is-mobile': isBreakpoint,
        'is-opened': showMenu,
    });

    React.useEffect(() => {
        if (isOpened) return;

        setMenuVisible(isOpened);
    }, [isOpened]);

    React.useEffect(() => {
        if (showMenu) {
            document.body.classList.add('menu-opened');
        }

        if (onChange) {
            onChange(showMenu);
        }

        return () => {
            document.body.classList.remove('menu-opened');
        };
    }, [showMenu, onChange]);

    React.useEffect(() => {
        if (router.route === '/[lang]' || router.route === '/') {
            setAnchorLinks(true);
        } else {
            setAnchorLinks(false);
        }
    }, [router]);

    return (
        <FocusTrap
            focusTrapOptions={{
                escapeDeactivates: true,
                onDeactivate: () => setMenuVisible(false),
                allowOutsideClick: true,
            }}
            active={isBreakpoint && showMenu}
        >
            <nav aria-label={navigationLexemes.lxNavigationA11yLabel()}>
                {isBreakpoint && (
                    <button
                        className="page-navigation__toggle"
                        onClick={() => setMenuVisible(!showMenu)}
                        type="button"
                        aria-expanded={Boolean(showMenu)}
                    >
                        <span className="page-navigation__toggle-icon">
                            {showMenu ? (
                                <Icon
                                    name={IconName.Close}
                                    size="stretch"
                                    a11y={{ label: navigationLexemes.lxNavigationCloseA11yLabel() }}
                                />
                            ) : (
                                <Icon
                                    name={IconName.Menu}
                                    size="stretch"
                                    a11y={{ label: navigationLexemes.lxNavigationOpenA11yLabel() }}
                                />
                            )}
                        </span>
                    </button>
                )}

                <div className={pageNavigationClassnames}>
                    <ul className="page-navigation__list" role="list">
                        {items &&
                            items.map((item, index) => {
                                const { url, title, isHidden } = item;

                                const titleLexeme = !isHidden && <Style>{title}</Style>;

                                // exception for handbook
                                if (item.type === 'handbook' && url) {
                                    return (
                                        <li
                                            className="page-navigation__item"
                                            key={`navigation-item-handbook`}
                                        >
                                            <ActiveLink activeClassName="active">
                                                <a
                                                    className="page-navigation__link"
                                                    target="_blank"
                                                    href={url}
                                                >
                                                    {titleLexeme}

                                                    <A11yVisuallyHidden>
                                                        <Lexeme path="mw@link.a11y.label.new-window" />
                                                    </A11yVisuallyHidden>
                                                </a>
                                            </ActiveLink>
                                        </li>
                                    );
                                }

                                return (
                                    <li
                                        className={cx({
                                            'page-navigation__item': true,
                                            'is-hidden': isHidden,
                                        })}
                                        key={`navigation-item-${index}`}
                                    >
                                        {anchorLinks ? (
                                            <>
                                                {/* @ts-ignore */}
                                                <Link
                                                    className="page-navigation__link"
                                                    activeClass="is-active"
                                                    spy={true}
                                                    smooth={true}
                                                    delay={50}
                                                    duration={500}
                                                    onClick={() => {
                                                        if (!isBreakpoint) return;

                                                        setMenuVisible(false);
                                                    }}
                                                    onSetActive={onSetActive}
                                                    to={url as string}
                                                    offset={_offset}
                                                    href={`#${url}`}
                                                >
                                                    {titleLexeme}
                                                </Link>
                                            </>
                                        ) : (
                                            <ActiveLink activeClassName="active">
                                                <a
                                                    className="page-navigation__link"
                                                    href={`/${lang}#${url}`}
                                                >
                                                    {titleLexeme}
                                                </a>
                                            </ActiveLink>
                                        )}
                                    </li>
                                );
                            })}
                    </ul>
                </div>
            </nav>
        </FocusTrap>
    );
};

export default PageNavigation;
